/**
 * Streaming TTS Store (Zustand)
 *
 * Manages the lifecycle of server-fed streaming text-to-speech.
 * Tokens from the conversation stream are fed to Azure Speech SDK
 * for progressive audio synthesis.
 *
 * Separate from ttsStore.ts which handles one-shot manual playback.
 * When streaming TTS fails, falls back to ttsStore (native one-shot)
 * or expo-speech (device TTS).
 */

import { create } from 'zustand';
import { createDeviceStreamingTTSSession, createServerStreamingTTSSession, type StreamingTTSCallbacks, type StreamingTTSSession, type StreamingTTSStatus } from '../services/streamingTTS';
import { getActiveCoursePreference, useUserPreferencesStore } from './userPreferencesStore';
import { createLogger } from '../utils/logger';
import { takeAudioFocus, releaseAudioFocus } from '../services/audioFocus';

const log = createLogger('StreamingTTSStore');

interface StreamingTTSState {
  /** Current status of the streaming TTS session */
  status: StreamingTTSStatus;

  /** Message ID being spoken (for UI state, e.g., speaker icon animation) */
  activeMessageId: string | null;

  /** Full text accumulated so far (for fallback if streaming fails) */
  accumulatedText: string;

  /**
   * Start a streaming TTS session for a message.
   * Fetches speech token and initializes the native session.
   * `messageId` is the caller's OPTIMISTIC local id during auto-play;
   * `sessionId` (the chat session) is what keeps the server-TTS cost
   * attributable while the real message id doesn't exist yet (ADR-026).
   */
  startStreaming: (messageId: string, sessionId?: string) => Promise<void>;

  /**
   * Feed a text token from the conversation stream.
   * Called on each Socket.IO 'token' event when autoPlay is enabled.
   */
  feedToken: (text: string) => void;

  /**
   * Signal that text streaming is complete. Flushes remaining audio.
   * Called on Socket.IO 'done' event.
   */
  finishStreaming: () => Promise<void>;

  /**
   * Cancel the active session immediately (interruption).
   */
  cancel: () => void;

  /**
   * Full cleanup — cancel + reset state.
   * Call on ChatScreen unmount.
   */
  cleanup: () => void;
}

let activeSession: StreamingTTSSession | null = null;
/** finishStreaming() has been called for the current message. */
let finishRequested = false;
/**
 * Pending server→device fallback swap (ATTS-004): finishStreaming must wait
 * for the fallback session to be started and seeded before finishing it,
 * otherwise a 'done' event racing the swap finishes an empty session.
 */
let fallbackTransition: Promise<void> | null = null;

export const useStreamingTTSStore = create<StreamingTTSState>((set, get) => ({
  status: 'idle',
  activeMessageId: null,
  accumulatedText: '',

  startStreaming: async (messageId: string, sessionId?: string) => {
    // Stop one-shot TTS if playing (user may have tapped speaker on another message)
    // Lazy require to avoid circular dependency (ttsStore imports streamingTTSStore)
    const { useTTSStore } = require('./ttsStore');
    useTTSStore.getState().stop();

    // Cancel any existing streaming session
    if (activeSession) {
      activeSession.dispose();
      activeSession = null;
    }

    const coursePref = getActiveCoursePreference();
    const provider = coursePref?.ttsProvider ?? 'azure-server';

    finishRequested = false;
    fallbackTransition = null;
    set({ status: 'starting', activeMessageId: messageId, accumulatedText: '' });
    // Claim audio focus — pauses the Audio Lessons player if it held it (one
    // output at a time). Yields back by cancelling this streaming session.
    takeAudioFocus('chat-tts', () => get().cancel());

    // TTS_TTFA — permanent A/B instrument, one event per trigger
    // (server-autoplay-tts.mobile.md ATTS-005). PII-free: no message text.
    const ttfaStart = Date.now();
    let ttfaLogged = false;
    const logTtfa = (audioProvider: string, extra?: Record<string, unknown>) => {
      if (ttfaLogged) return;
      ttfaLogged = true;
      log.info('TTS_TTFA', {
        provider: audioProvider,
        ms: Date.now() - ttfaStart,
        surface: 'autoplay',
        // Total text accumulated by first-audio (a rough length signal; for
        // auto-play the FIRST segment drives first-audio, not the whole reply).
        chars: get().accumulatedText.length,
        ...extra,
      });
    };

    const { targetLocale } = useUserPreferencesStore.getState();

    const sessionCallbacks: StreamingTTSCallbacks = {
      onStatusChange: (status: StreamingTTSStatus) => {
        set({ status });
      },
      onError: (error: Error) => {
        log.error('Streaming TTS error', error);
        activeSession = null;
        releaseAudioFocus('chat-tts');
      },
      onComplete: () => {
        log.info('Streaming TTS completed');
        activeSession = null;
        set({ activeMessageId: null });
        releaseAudioFocus('chat-tts');
      },
      onFirstAudio: () => logTtfa(provider),
    };

    // One-time device fallback for the server session (ATTS-004): swap in a
    // device session seeded with the text accumulated so far. Subsequent
    // feedToken/finishStreaming route to it via the activeSession indirection.
    const startDeviceFallback = (failedSession: StreamingTTSSession) => {
      if (activeSession !== failedSession) return; // canceled or superseded
      log.warn('Server TTS session failed — falling back to device TTS');
      const fallbackSession = createDeviceStreamingTTSSession(
        {
          ...sessionCallbacks,
          onFirstAudio: () => logTtfa('device', { fallback: true }),
        },
        targetLocale,
      );
      activeSession = fallbackSession;
      fallbackTransition = (async () => {
        await fallbackSession.start('', '', '');
        if (activeSession !== fallbackSession) return;
        const seed = get().accumulatedText;
        if (seed) fallbackSession.feedToken(seed);
        if (finishRequested) await fallbackSession.finish();
      })().catch((error: unknown) => {
        // Defensive — the device session reports failures via callbacks,
        // not rejections. Never leave finishStreaming awaiting a rejection.
        log.error('Device TTS fallback failed', error instanceof Error ? error : new Error(String(error)));
      });
    };

    let session: StreamingTTSSession;
    if (provider === 'device') {
      session = createDeviceStreamingTTSSession(sessionCallbacks, targetLocale);
    } else {
      let fellBack = false;
      session = createServerStreamingTTSSession(
        {
          ...sessionCallbacks,
          onStatusChange: (status: StreamingTTSStatus) => {
            // Swallow the server session's transient 'error' — the fallback
            // session's own statuses flow next (no speaker-button flicker).
            if (status === 'error') return;
            set({ status });
          },
          onError: (error: Error) => {
            log.warn('Server streaming TTS error — attempting device fallback', error);
            if (fellBack) return;
            fellBack = true;
            startDeviceFallback(session);
          },
        },
        { voice: coursePref?.azureTtsVoice, messageId, sessionId },
      );
    }

    activeSession = session;

    try {
      // No Azure credential on either path — the server path synthesizes
      // backend-side (POST /api/speech/tts) and the device path uses expo-speech.
      await session.start('', '', '');
    } catch (error) {
      log.error('Failed to initialize streaming TTS session', error instanceof Error ? error : new Error(String(error)));
      set({ status: 'error', activeMessageId: null });
      activeSession = null;
    }
  },

  feedToken: (text: string) => {
    if (!activeSession || !activeSession.isActive) return;
    activeSession.feedToken(text);
    // Accumulate text for fallback
    set((state) => ({ accumulatedText: state.accumulatedText + text }));
  },

  finishStreaming: async () => {
    finishRequested = true;
    // A server→device fallback swap may be mid-flight — wait for the
    // fallback session to be started and seeded before finishing it.
    if (fallbackTransition) await fallbackTransition;
    if (!activeSession) return;
    await activeSession.finish();
  },

  cancel: () => {
    if (activeSession) {
      activeSession.dispose();
      activeSession = null;
    }
    set({ status: 'canceled', activeMessageId: null, accumulatedText: '' });
    releaseAudioFocus('chat-tts');
  },

  cleanup: () => {
    get().cancel();
  },
}));
